import { computed, reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import ToastEventBus from 'primevue/toasteventbus'
import { useLocalStorage } from '@/lib/utils/useLocalStorage'
import {
  NotificationPosition,
  NotificationType,
  type AlertOptions,
  type NotificationDriver,
  type SiteNotification,
} from '@/lib/types'

const MAX_NOTIFICATIONS = 5
const DEFAULT_TIMEOUT_MS = 3000
const DRIVER_KEY = 'lockerserver:notification-driver'

/**
 * Toast notification store — single API surface, two drivers.
 *
 * Render path:
 *   - `driver === 'pv'`     — emits to `ToastEventBus`. The PrimeVue
 *     `<Toast />` host (mounted in `App.vue`) renders via our custom
 *     `#container` slot so the chrome matches the native cards.
 *   - `driver === 'native'` — rendered by the `<Notification />`
 *     partial (in `layouts/partials/`) iterating the store's list.
 *
 * Driver-independent behaviour stays in this store:
 *   - in-memory `notifications` Map (insertion-order preserved)
 *   - auto-dismiss `setTimeout` per entry
 *   - pause-on-hover (recomputes remaining ms from
 *     `Date.now() - startedAt`)
 *   - 5-item cap with oldest-evict
 *   - CSS-driven progress bar (`--notif-duration` var consumed by
 *     both renderers)
 *
 * `ToastEventBus` is PrimeVue's underlying message bus — using it
 * directly (instead of `useToast()`'s `inject` API) avoids the
 * "must be in setup" constraint, so callers (error handlers,
 * one-off utilities) can fire notifications from anywhere.
 *
 * The PV driver path emits with `life: 0` so PrimeVue's own auto-
 * dismiss timer is disabled — this store remains the single owner
 * of lifetime + pause semantics.
 */
/**
 * Subset of PrimeVue's ToastMessageOptions we actually emit. Typed
 * locally to avoid pulling in PrimeVue's type from a separate package
 * that doesn't export a clean public Message type for v4.
 */
interface PvMessage {
  severity: string
  summary?: string
  detail?: string
  life: number
  closable: boolean
  data: { notificationId: string }
}

export const useNotificationStore = defineStore('notification', () => {
  const notifications = reactive<Map<string, SiteNotification>>(new Map())
  const position = ref<NotificationPosition>(NotificationPosition.TOP_RIGHT)

  /**
   * PV driver bookkeeping — original message references PrimeVue's
   * internal `messages[]` array holds. PrimeVue's `Toast.onRemove`
   * matches by `m === message || m.id === message.id`. Our remove
   * emit MUST pass the SAME REFERENCE we passed at add-time;
   * otherwise PrimeVue can't locate the entry, leaves the
   * `.p-toast-message` wrapper in the DOM (a real leak) and stacks
   * subsequent toasts below the orphan space. NOT reactive — pure
   * lookup table; Vue tracking would cost re-renders for nothing.
   */
  const pvMessageRefs = new Map<string, PvMessage>()

  /**
   * Store-wide default driver. PrimeVue is the default (better
   * animations + production-grade overlay handling). `setDriver`
   * mutates this; per-call overrides flow through `AlertOptions.driver`
   * and do NOT touch this ref.
   */
  const driver = useLocalStorage<NotificationDriver>(DRIVER_KEY, 'pv')

  const notificationList = computed(() => Array.from(notifications.values()))

  /** Subset rendered by the native `<Notification />` host. */
  const nativeNotifications = computed(() =>
    notificationList.value.filter((n) => n.driver === 'native'),
  )

  const setDriver = (next: NotificationDriver): void => {
    driver.value = next
    // Persistence handled by `useLocalStorage`'s watcher.
  }

  const usePosition = (pos: NotificationPosition): void => {
    position.value = pos
  }

  const pvSeverityFor = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.SUCCESS:
        return 'success'
      case NotificationType.ERROR:
        return 'error'
      case NotificationType.WARNING:
        return 'warn'
      case NotificationType.INFO:
        return 'info'
    }
  }

  const pushNotification = (
    payload: Omit<SiteNotification, 'id' | 'driver'>,
    opts: AlertOptions = {},
  ): string => {
    const id = uuidv4()
    const timeout = opts.timeout ?? payload.timeout ?? DEFAULT_TIMEOUT_MS
    // Per-call override doesn't mutate the store-wide default.
    const effectiveDriver = opts.driver ?? driver.value

    // Evict oldest if at capacity (across BOTH drivers — the cap
    // is on the in-memory list, not per-renderer).
    if (notifications.size >= MAX_NOTIFICATIONS) {
      const firstKey = notifications.keys().next().value as string | undefined
      if (firstKey) removeNotification(firstKey)
    }

    const entry: SiteNotification = {
      id,
      ...payload,
      driver: effectiveDriver,
      timeout,
      paused: false,
    }
    notifications.set(id, entry)

    if (timeout > 0) {
      entry.startedAt = Date.now()
      entry.remaining = timeout
      entry.timer = window.setTimeout(() => removeNotification(id), timeout)
    }

    // PrimeVue render path — emit to the bus with our id stashed in
    // `data` so the host's `#container` slot can look the entry up
    // for the progress bar + pause-on-hover wiring. `life: 0`
    // disables PV's own auto-dismiss; this store stays the single
    // owner of lifetime.
    //
    // CRITICAL: cache the EXACT message object emitted at add-time
    // (`pvMessageRefs`). PrimeVue's `Toast.onRemove` matches by
    // reference equality, so remove MUST pass the same ref — see
    // `removeNotification` below + the kdoc on `pvMessageRefs`.
    if (effectiveDriver === 'pv') {
      const pvMessage: PvMessage = {
        severity: pvSeverityFor(entry.type),
        summary: entry.title,
        detail: entry.text,
        life: 0,
        closable: true,
        data: { notificationId: id },
      }
      pvMessageRefs.set(id, pvMessage)
      ToastEventBus.emit('add', pvMessage)
    }

    return id
  }

  const removeNotification = (id: string): void => {
    const entry = notifications.get(id)
    if (!entry) return
    if (entry.timer !== undefined) window.clearTimeout(entry.timer)
    // PV entries: pass the ORIGINAL message ref that PrimeVue
    // stored at add-time. Without this PrimeVue's onRemove can't
    // match the entry (reference equality fails on a freshly-built
    // object) and the `.p-toast-message` wrapper leaks into the
    // DOM. See `pvMessageRefs` kdoc.
    if (entry.driver === 'pv') {
      const pvMessage = pvMessageRefs.get(id)
      if (pvMessage) {
        ToastEventBus.emit('remove', pvMessage)
        pvMessageRefs.delete(id)
      }
    }
    notifications.delete(id)
  }

  /**
   * Freeze the auto-dismiss timer + CSS animation. Records how
   * much time was left so `resumeTimeout` can pick up exactly
   * where we left off (no jump-to-start, no double-time).
   * Driver-independent — both renderers feed the same id back.
   */
  const pauseTimeout = (id: string): void => {
    const entry = notifications.get(id)
    if (!entry || !entry.timer || !entry.startedAt) return
    const elapsed = Date.now() - entry.startedAt
    entry.remaining = Math.max(0, (entry.remaining ?? entry.timeout ?? 0) - elapsed)
    window.clearTimeout(entry.timer)
    entry.timer = undefined
    entry.paused = true
  }

  /** Resume after pause — re-arm the timer with the remembered remaining ms. */
  const resumeTimeout = (id: string): void => {
    const entry = notifications.get(id)
    if (!entry || entry.timer !== undefined) return
    const remaining = entry.remaining ?? entry.timeout ?? 0
    if (remaining <= 0) {
      removeNotification(id)
      return
    }
    entry.startedAt = Date.now()
    entry.timer = window.setTimeout(() => removeNotification(id), remaining)
    entry.paused = false
  }

  // ── Convenience helpers ─────────────────────────────────────────
  // Each accepts `opts` for per-call driver / timeout override.
  // Severity-specific default timeouts mirror the previous API.
  const alertSuccess = (text: string, title?: string, opts?: AlertOptions): string =>
    pushNotification({ type: NotificationType.SUCCESS, title, text }, opts)

  const alertError = (text: string, title?: string, opts?: AlertOptions): string =>
    pushNotification(
      { type: NotificationType.ERROR, title, text, timeout: opts?.timeout ?? 6000 },
      opts,
    )

  const alertWarning = (text: string, title?: string, opts?: AlertOptions): string =>
    pushNotification(
      { type: NotificationType.WARNING, title, text, timeout: opts?.timeout ?? 5000 },
      opts,
    )

  const alertInfo = (text: string, title?: string, opts?: AlertOptions): string =>
    pushNotification({ type: NotificationType.INFO, title, text }, opts)

  return {
    notifications,
    notificationList,
    nativeNotifications,
    position,
    driver,
    setDriver,
    usePosition,
    pushNotification,
    removeNotification,
    pauseTimeout,
    resumeTimeout,
    alertSuccess,
    alertError,
    alertWarning,
    alertInfo,
  }
})
